import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { Badge, FailureBadge, Loading, Meter, useAsync } from '../components/common';

export default function DashboardPage() {
  const { data, error, loading } = useAsync(() => api.dashboard(), []);
  if (loading || !data) return <Loading error={error} />;

  const { counts } = data;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Dashboard</h1>
          <p>
            Estate health for Centralized DSP. Edit parents and bindings here, publish a snapshot,
            and Lambdas read that snapshot — not hardcoded maps. Start with the config matrix for
            full visibility of what each function uses per parent.
          </p>
        </div>
      </div>

      <div className="grid" style={{ marginBottom: 18 }}>
        <Link className="stat" to="/settlement" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="n">Settlement</div>
          <div className="l">Operations functions</div>
        </Link>
        <Link
          className="stat"
          to="/lead-scoring"
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <div className="n">Lead Scoring</div>
          <div className="l">Eligibility &amp; LendAPI</div>
        </Link>
        <Link className="stat" to="/general" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="n">General</div>
          <div className="l">Shared tooling</div>
        </Link>
        <Link className="stat" to="/matrix" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="n">Matrix</div>
          <div className="l">Parent × function wiring</div>
        </Link>
      </div>

      <div className="grid" style={{ marginBottom: 18 }}>
        <Stat n={counts.parents_active} l="Active parents" />
        <Stat n={counts.parents_draft} l="Draft parents" />
        <Stat n={counts.lambdas_partner_aware} l="Partner-aware lambdas" />
        <Stat n={counts.lambdas_total} l="Lambdas registered" />
        <Stat
          n={data.latest_publication ? `v${data.latest_publication.version}` : '—'}
          l="Published config"
        />
      </div>

      <div className="card">
        <h2>
          Lambdas missing bindings for active parents
          <span className="hint">a required gap blocks publish</span>
        </h2>
        {data.missing_bindings.length === 0 ? (
          <div className="callout ok">Every active parent is bound to every partner-aware Lambda.</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Lambda</th>
                  <th>Required</th>
                  <th>Completeness</th>
                  <th>Unbound parents</th>
                </tr>
              </thead>
              <tbody>
                {data.missing_bindings.map((row) => (
                  <tr key={row.lambda_key}>
                    <td>
                      <Link to={`/lambdas/${row.lambda_key}`}>{row.display_name}</Link>
                      <div className="small muted mono">{row.lambda_key}</div>
                    </td>
                    <td>
                      {row.required ? <Badge tone="danger">required</Badge> : <Badge>optional</Badge>}
                    </td>
                    <td>
                      <Meter value={row.config_completeness} />
                    </td>
                    <td>
                      <div className="chips">
                        {row.unbound_parents.map((p) => (
                          <Badge key={p} tone={row.required ? 'danger' : 'default'}>
                            {p}
                          </Badge>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>
          Dangerous fallbacks still in code
          <span className="hint">cleared only when the Phase 6 fix lands</span>
        </h2>
        {data.dangerous_fallbacks.length === 0 ? (
          <div className="callout ok">Every registered Lambda fails closed.</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Lambda</th>
                  <th>Behaviour</th>
                  <th>What goes wrong</th>
                </tr>
              </thead>
              <tbody>
                {data.dangerous_fallbacks.map((row) => (
                  <tr key={row.lambda_key}>
                    <td>
                      <Link to={`/lambdas/${row.lambda_key}`}>{row.display_name}</Link>
                    </td>
                    <td>
                      <FailureBadge mode={row.failure_mode} />
                    </td>
                    <td className="small">{row.danger_flags[0] ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>
          EventBridge coverage gaps
          <span className="hint">inbound folders nothing is watching</span>
        </h2>
        {data.eventbridge_gaps.length === 0 ? (
          <div className="callout ok">Every active parent's inbound folders have a watcher.</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Parent</th>
                  <th>Bucket EventBridge</th>
                  <th>Missing watchers</th>
                </tr>
              </thead>
              <tbody>
                {data.eventbridge_gaps.map((row) => (
                  <tr key={row.parent_key}>
                    <td>
                      <Link to={`/parents/${row.parent_key}`}>{row.display_name}</Link>
                    </td>
                    <td>
                      {row.bucket_eventbridge_enabled ? (
                        <Badge tone="ok">enabled</Badge>
                      ) : (
                        <Badge tone="danger">not enabled on the bucket</Badge>
                      )}
                    </td>
                    <td>
                      <div className="chips">
                        {row.missing_watchers.map((w) => (
                          <Badge key={w} tone="warn">
                            {w}
                          </Badge>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="small muted" style={{ marginBottom: 0 }}>
          Bucket-level EventBridge is owned by <code>infra/s3_event_foundation</code>. This service
          never writes S3 bucket notification configuration — an SM6 apply once wiped a shared
          bucket's EventBridge config and silently took SM0 and SM2 offline.
        </p>
      </div>
    </>
  );
}

function Stat({ n, l }: { n: number | string; l: string }) {
  return (
    <div className="stat">
      <div className="n">{n}</div>
      <div className="l">{l}</div>
    </div>
  );
}
